import React, { useState, useEffect, useRef } from 'react';
import fuelService, { FuelStats, RealtimeData, FuelDataResponse, ConsumptionResponse } from '../../services/fuelService';
import fuelTrackingService, { TrackingSessionResponse } from '../../services/fuelTrackingService';
import authService from '../../services/authService';
import './FuelTracking.css';

interface FuelTrackingProps {
  carId: number;
  carName: string;
  imei?: string;
}

const FuelTracking: React.FC<FuelTrackingProps> = ({ carId, carName, imei }) => {
  const [isTracking, setIsTracking] = useState(false);
  const [startTime, setStartTime] = useState<string | null>(null);
  const [endTime, setEndTime] = useState<string | null>(null);
  const [fuelStats, setFuelStats] = useState<FuelStats | null>(null);
  const [consumptionData, setConsumptionData] = useState<ConsumptionResponse | null>(null);
  const [fuelData, setFuelData] = useState<FuelDataResponse | null>(null);
  const [realtimeData, setRealtimeData] = useState<RealtimeData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentFuelLevel, setCurrentFuelLevel] = useState<number | null>(null);
  const [initialFuelLevel, setInitialFuelLevel] = useState<number | null>(null);
  const [finalFuelLevel, setFinalFuelLevel] = useState<number | null>(null);
  const [fuelConsumption, setFuelConsumption] = useState<number>(0);
  const [trackingDuration, setTrackingDuration] = useState<string>('00:00:00');
  
  // Новые состояния для работы с сессиями
  const [currentSessionId, setCurrentSessionId] = useState<number | null>(null);
  const [sessionHistory, setSessionHistory] = useState<TrackingSessionResponse[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);
  const [initialLevelSource, setInitialLevelSource] = useState<string>('Не установлен');
  
  // Состояния для накопления данных
  const [allFuelData, setAllFuelData] = useState<FuelDataResponse[]>([]);
  const [allFuelStats, setAllFuelStats] = useState<FuelStats[]>([]);
  const [allConsumptionData, setAllConsumptionData] = useState<ConsumptionResponse[]>([]);
  const [allRealtimeData, setAllRealtimeData] = useState<RealtimeData[]>([]);
  
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<Date | null>(null);

  // Проверка авторизации
  const checkAuthorization = () => {
    const token = authService.getAccessToken();
    const authorized = !!token;
    setIsAuthorized(authorized);
    console.log('FuelTracking - Authorization check:', authorized);
    return authorized;
  };

  // Получение IMEI из данных машины (если не передан)
  const getImei = async () => {
    if (imei) return imei;
    
    try {
      // Здесь можно добавить логику получения IMEI по carId
      // Пока используем заглушку
      return '866795039055551';
    } catch (error) {
      console.error('Error getting IMEI:', error);
      return null;
    }
  };

  // Проверка активной сессии при загрузке компонента
  const checkActiveSession = async () => {
    // Проверяем авторизацию перед запросом к API
    if (!checkAuthorization()) {
      console.log('FuelTracking - User not authorized, skipping session check');
      return;
    }

    try {
      const activeSession = await fuelTrackingService.getActiveSession(carId);
      if (activeSession) {
        setCurrentSessionId(activeSession.id);
        setIsTracking(true);
        setStartTime(activeSession.startTime);
        startTimeRef.current = new Date(activeSession.startTime);
        
        // Восстанавливаем состояние отслеживания
        await fetchRealtimeData();
        
        // Запускаем периодическое обновление
        intervalRef.current = setInterval(() => {
          fetchRealtimeData();
          updateTrackingDuration();
        }, 5000); // Обновляем каждые 5 секунд
      }
    } catch (error) {
      console.error('Error checking active session:', error);
      // Не показываем ошибку пользователю, просто работаем без API сессий
    }
  };

  // Загрузка истории сессий
  const loadSessionHistory = async () => {
    // Проверяем авторизацию перед запросом к API
    if (!checkAuthorization()) {
      console.log('FuelTracking - User not authorized, skipping history load');
      return;
    }

    try {
      const sessions = await fuelTrackingService.getSessionsByCar(carId);
      setSessionHistory(sessions);
    } catch (error) {
      console.error('Error loading session history:', error);
      // Не показываем ошибку пользователю, просто оставляем пустую историю
    }
  };

  // Получение текущих данных топлива
  const fetchRealtimeData = async () => {
    try {
      const currentImei = await getImei();
      if (!currentImei) return;

      const data = await fuelService.getRealtimeData(currentImei, 1);
      
      // Обновляем текущие данные
      setRealtimeData(data);
      
      // Накопляем данные в массив
      setAllRealtimeData(prev => [...prev, data]);
      
      // Получаем текущий уровень топлива из consumptionStats
      if (data.consumptionStats && data.consumptionStats.sensors) {
        const sensors = data.consumptionStats.sensors;
        if (sensors && typeof sensors === 'object') {
          const totalCurrentLevel = Object.values(sensors)
            .reduce((sum, sensor) => sum + (sensor?.currentLevel || 0), 0);
          setCurrentFuelLevel(totalCurrentLevel);
          console.log('Текущий уровень топлива:', totalCurrentLevel);
          
          // Устанавливаем начальный уровень из ПЕРВЫХ данных реального времени
          if (isTracking && initialFuelLevel === null && totalCurrentLevel > 0) {
            setInitialFuelLevel(totalCurrentLevel);
            setInitialLevelSource('Первые данные реального времени');
            console.log('✅ Установлен начальный уровень из первых данных реального времени:', totalCurrentLevel);
          }
          
          // Дополнительно: если начальный уровень не установлен, но есть данные с более высоким уровнем,
          // устанавливаем максимальный уровень как начальный
          if (isTracking && initialFuelLevel === null && totalCurrentLevel > 0) {
            // Проверяем все накопленные данные на предмет максимального уровня
            let maxLevel = totalCurrentLevel;
            allRealtimeData.forEach(data => {
              if (data.consumptionStats && data.consumptionStats.sensors) {
                const sensors = data.consumptionStats.sensors;
                if (sensors && typeof sensors === 'object') {
                  const level = Object.values(sensors)
                    .reduce((sum, sensor) => sum + (sensor?.currentLevel || 0), 0);
                  if (level > maxLevel) {
                    maxLevel = level;
                  }
                }
              }
            });
            
            if (maxLevel > totalCurrentLevel) {
              setInitialFuelLevel(maxLevel);
              setInitialLevelSource('Максимум из накопленных данных');
              console.log('✅ Установлен начальный уровень из максимального значения (включая накопленные данные):', maxLevel);
            } else {
              setInitialFuelLevel(totalCurrentLevel);
              setInitialLevelSource('Первые данные реального времени');
              console.log('✅ Установлен начальный уровень из текущих данных реального времени:', totalCurrentLevel);
            }
          }
        }
      }

    } catch (error) {
      console.error('Error fetching realtime data:', error);
      setError('Ошибка при получении данных топлива');
    }
  };

  // Получение данных топлива за период
  const fetchFuelDataForPeriod = async () => {
    if (!startTime || !endTime) return;

    try {
      setLoading(true);
      setError(null);

      const currentImei = await getImei();
      if (!currentImei) return;

      // Получаем данные топлива за период
      const data = await fuelService.getFuelData(currentImei, startTime, endTime);
      setFuelData(data);
      setAllFuelData(prev => [...prev, data]);

      // Получаем статистику за период
      const stats = await fuelService.getFuelStats(currentImei, startTime, endTime);
      setFuelStats(stats);
      setAllFuelStats(prev => [...prev, stats]);

      // Получаем статистику потребления за период
      const consumption = await fuelService.getFuelConsumption(currentImei, startTime, endTime);
      setConsumptionData(consumption);
      setAllConsumptionData(prev => [...prev, consumption]);

      // Устанавливаем начальный уровень из первых исторических данных
      if (data.data && data.data.length > 0 && initialFuelLevel === null) {
        const firstDataPoint = data.data[0];
        const initialLevel = firstDataPoint.liters || 0;
        setInitialFuelLevel(initialLevel);
        setInitialLevelSource('Исторические данные');
        console.log('✅ Установлен начальный уровень из исторических данных:', initialLevel);
      }

      // Устанавливаем конечный уровень из последних исторических данных
      if (data.data && data.data.length > 0) {
        const lastDataPoint = data.data[data.data.length - 1];
        const finalLevel = lastDataPoint.liters || 0;
        setFinalFuelLevel(finalLevel);
        console.log('✅ Установлен конечный уровень из исторических данных:', finalLevel);
      }

      // Рассчитываем расход топлива как разность между начальным и конечным уровнем
      if (initialFuelLevel !== null && finalFuelLevel !== null) {
        const consumption = initialFuelLevel - finalFuelLevel;
        setFuelConsumption(consumption > 0 ? consumption : 0);
        console.log('📊 Расчет расхода из исторических данных:');
        console.log('  - Начальный уровень (история):', initialFuelLevel);
        console.log('  - Конечный уровень (история):', finalFuelLevel);
        console.log('  - Расход:', consumption);
        console.log('  - Установленный расход:', consumption > 0 ? consumption : 0);
      }

    } catch (error) {
      console.error('Error fetching fuel data for period:', error);
      setError('Ошибка при получении данных топлива за период');
    } finally {
      setLoading(false);
    }
  };

  // Запуск отслеживания
  const handleStartTracking = async () => {
    if (isTracking) {
      return; // Не запускаем если отслеживание уже активно
    }

    try {
      setLoading(true);
      setError(null);

      // Останавливаем предыдущее отслеживание если оно активно
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      const currentImei = await getImei();
      if (!currentImei) {
        setError('Не удалось получить IMEI устройства');
        return;
      }

      const now = new Date();
      setStartTime(now.toISOString());
      setEndTime(null);
      startTimeRef.current = now;
      setIsTracking(true);
      
      // Очищаем все старые данные
      setInitialFuelLevel(null);
      setFinalFuelLevel(null);
      setFuelConsumption(0);
      setFuelData(null);
      setFuelStats(null);
      setConsumptionData(null);
      setTrackingDuration('00:00:00');
      setInitialLevelSource('Не установлен');
      
      // Очищаем накопленные данные
      setAllFuelData([]);
      setAllFuelStats([]);
      setAllConsumptionData([]);
      setAllRealtimeData([]);

      // Пытаемся создать новую сессию отслеживания
      try {
        const session = await fuelTrackingService.createSession({
          carId: carId,
          carName: carName,
          imei: currentImei
        });
        setCurrentSessionId(session.id);
      } catch (sessionError) {
        console.error('Error creating session:', sessionError);
        // Если не удалось создать сессию, продолжаем без неё
        setCurrentSessionId(null);
      }

      // Получаем данные сразу для установки начального уровня
      await fetchRealtimeData();
      
      // Начальный уровень будет установлен из исторических данных при остановке отслеживания

      // Запускаем периодическое обновление данных
      intervalRef.current = setInterval(() => {
        fetchRealtimeData();
        updateTrackingDuration();
      }, 5000); // Обновляем каждые 5 секунд

    } catch (error) {
      console.error('Error starting tracking:', error);
      setError('Ошибка при запуске отслеживания');
    } finally {
      setLoading(false);
    }
  };

  // Остановка отслеживания
  const handleStopTracking = async () => {
    if (!isTracking) {
      return; // Не останавливаем если отслеживание не активно
    }

    try {
      setLoading(true);
      setError(null);

      const now = new Date();
      const endTimeStr = now.toISOString();
      setEndTime(endTimeStr);
      setIsTracking(false);

      // Останавливаем периодическое обновление
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      // Получаем данные за период (это установит правильные начальный и конечный уровни)
      await fetchFuelDataForPeriod();

      // Устанавливаем начальный и конечный уровни из накопленных данных реального времени
      if (allRealtimeData.length > 0) {
        let maxLevel = 0;
        let minLevel = Infinity;
        
        allRealtimeData.forEach(data => {
          if (data.consumptionStats && data.consumptionStats.sensors) {
            const sensors = data.consumptionStats.sensors;
            if (sensors && typeof sensors === 'object') {
              const totalLevel = Object.values(sensors)
                .reduce((sum, sensor) => sum + (sensor?.currentLevel || 0), 0);
              if (totalLevel > 0) {
                if (totalLevel > maxLevel) {
                  maxLevel = totalLevel;
                }
                if (totalLevel < minLevel) {
                  minLevel = totalLevel;
                }
              }
            }
          }
        });
        
        // Устанавливаем начальный уровень как максимальный из всех записей
        if (maxLevel > 0) {
          setInitialFuelLevel(maxLevel);
          setInitialLevelSource('Максимум из накопленных данных');
          console.log('✅ Установлен начальный уровень из максимального значения накопленных данных:', maxLevel);
        }
        
        // Устанавливаем конечный уровень как минимальный из всех записей (или текущий)
        if (minLevel < Infinity) {
          setFinalFuelLevel(minLevel);
          console.log('✅ Установлен конечный уровень из минимального значения накопленных данных:', minLevel);
        } else if (currentFuelLevel !== null) {
          setFinalFuelLevel(currentFuelLevel);
          console.log('✅ Установлен конечный уровень из текущих данных:', currentFuelLevel);
        }
      }

      // Если начальный уровень не установлен из данных реального времени, используем текущие
      if (initialFuelLevel === null && currentFuelLevel !== null) {
        setInitialFuelLevel(currentFuelLevel);
        setInitialLevelSource('Текущие данные (реальные данные недоступны)');
        console.log('⚠️ Установлен начальный уровень из текущих данных (реальные данные недоступны):', currentFuelLevel);
      }

      if (finalFuelLevel === null && currentFuelLevel !== null) {
        setFinalFuelLevel(currentFuelLevel);
        console.log('⚠️ Установлен конечный уровень из текущих данных (история недоступна):', currentFuelLevel);
      }

      // Рассчитываем расход топлива как разность между начальным и конечным уровнем
      if (initialFuelLevel !== null && finalFuelLevel !== null) {
        const consumption = initialFuelLevel - finalFuelLevel;
        setFuelConsumption(consumption > 0 ? consumption : 0);
        console.log('📊 Финальный расчет расхода:');
        console.log('  - Начальный уровень:', initialFuelLevel);
        console.log('  - Конечный уровень:', finalFuelLevel);
        console.log('  - Расход:', consumption);
        console.log('  - Установленный расход:', consumption > 0 ? consumption : 0);
      } else {
        console.warn('⚠️ Не удалось рассчитать расход:');
        console.log('  - initialFuelLevel:', initialFuelLevel);
        console.log('  - finalFuelLevel:', finalFuelLevel);
      }

      // Пытаемся завершить сессию отслеживания
      if (currentSessionId) {
        try {
          // Финальная проверка и установка значений для сессии
          let sessionInitialLevel = initialFuelLevel;
          let sessionFinalLevel = finalFuelLevel;
          
          // Если уровни не установлены, пытаемся их установить из накопленных данных
          if (sessionInitialLevel === null || sessionFinalLevel === null) {
            if (allRealtimeData.length > 0) {
              let maxLevel = 0;
              let minLevel = Infinity;
              
              allRealtimeData.forEach(data => {
                if (data.consumptionStats && data.consumptionStats.sensors) {
                  const sensors = data.consumptionStats.sensors;
                  if (sensors && typeof sensors === 'object') {
                    const totalLevel = Object.values(sensors)
                      .reduce((sum, sensor) => sum + (sensor?.currentLevel || 0), 0);
                    if (totalLevel > 0) {
                      if (totalLevel > maxLevel) {
                        maxLevel = totalLevel;
                      }
                      if (totalLevel < minLevel) {
                        minLevel = totalLevel;
                      }
                    }
                  }
                }
              });
              
              if (sessionInitialLevel === null && maxLevel > 0) {
                sessionInitialLevel = maxLevel;
              }
              
              if (sessionFinalLevel === null && minLevel < Infinity) {
                sessionFinalLevel = minLevel;
              }
            }
            
            // Если все еще не установлены, используем текущие данные
            if (sessionInitialLevel === null && currentFuelLevel !== null) {
              sessionInitialLevel = currentFuelLevel;
            }
            
            if (sessionFinalLevel === null && currentFuelLevel !== null) {
              sessionFinalLevel = currentFuelLevel;
            }
          }
          
          const finalConsumption = sessionInitialLevel !== null && sessionFinalLevel !== null 
            ? sessionInitialLevel - sessionFinalLevel 
            : 0;
          
          console.log('📤 Отправка данных в сессию:');
          console.log('  - sessionInitialLevel:', sessionInitialLevel);
          console.log('  - sessionFinalLevel:', sessionFinalLevel);
          console.log('  - fuelConsumption:', finalConsumption);
          console.log('  - allRealtimeData.length:', allRealtimeData.length);
          console.log('  - currentFuelLevel:', currentFuelLevel);
          
          // Показываем все уровни из накопленных данных для отладки
          if (allRealtimeData.length > 0) {
            console.log('  - Уровни из накопленных данных:');
            allRealtimeData.forEach((data, index) => {
              if (data.consumptionStats && data.consumptionStats.sensors) {
                const sensors = data.consumptionStats.sensors;
                if (sensors && typeof sensors === 'object') {
                  const totalLevel = Object.values(sensors)
                    .reduce((sum, sensor) => sum + (sensor?.currentLevel || 0), 0);
                  console.log(`    [${index}]: ${totalLevel} л`);
                }
              }
            });
          }
          
          await fuelTrackingService.completeSession(currentSessionId, {
            endTime: endTimeStr,
            initialFuelLevel: sessionInitialLevel || 0,
            finalFuelLevel: sessionFinalLevel || 0,
            fuelConsumption: finalConsumption > 0 ? finalConsumption : 0,
            fuelRefill: 0, // Можно добавить логику для пополнения
            netChange: -(finalConsumption > 0 ? finalConsumption : 0), // Отрицательное значение для расхода
            totalSensors: realtimeData?.consumptionStats?.totalSensors || 0,
            dataPointsCount: realtimeData?.dataPoints || 0,
            status: 'COMPLETED'
          });

          // Обновляем историю сессий
          await loadSessionHistory();
        } catch (sessionError) {
          console.error('Error completing session:', sessionError);
          // Если не удалось завершить сессию, продолжаем без неё
        }
      }

      // Очищаем ID сессии
      setCurrentSessionId(null);

    } catch (error) {
      console.error('Error stopping tracking:', error);
      setError('Ошибка при остановке отслеживания');
    } finally {
      setLoading(false);
    }
  };

  // Обновление длительности отслеживания
  const updateTrackingDuration = () => {
    if (startTimeRef.current) {
      const now = new Date();
      const diff = now.getTime() - startTimeRef.current.getTime();
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      setTrackingDuration(
        `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
      );
    }
  };

  // Очистка при размонтировании
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // Проверка активной сессии при загрузке
  useEffect(() => {
    // Проверяем авторизацию при загрузке
    checkAuthorization();
    checkActiveSession();
    loadSessionHistory();
  }, [carId]);

  // Обновление длительности каждую секунду при активном отслеживании
  useEffect(() => {
    let durationInterval: NodeJS.Timeout | null = null;
    
    if (isTracking) {
      durationInterval = setInterval(updateTrackingDuration, 1000);
    }

    return () => {
      if (durationInterval) {
        clearInterval(durationInterval);
      }
    };
  }, [isTracking]);

  // Автоматический расчет расхода при изменении уровней
  useEffect(() => {
    if (initialFuelLevel !== null && finalFuelLevel !== null) {
      const consumption = initialFuelLevel - finalFuelLevel;
      setFuelConsumption(consumption > 0 ? consumption : 0);
      console.log('🔄 Автоматический расчет расхода:');
      console.log('  - Начальный:', initialFuelLevel);
      console.log('  - Конечный:', finalFuelLevel);
      console.log('  - Расход:', consumption > 0 ? consumption : 0);
    }
  }, [initialFuelLevel, finalFuelLevel]);

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('ru-RU');
  };

  const formatLiters = (liters: number | null | undefined) => {
    if (liters === null || liters === undefined || isNaN(liters)) {
      return '—';
    }
    return `${liters.toFixed(2)} л`;
  };

  return (
    <div className="fuel-tracking">
      <div className="fuel-tracking-header">
        <h3>Отслеживание расхода топлива</h3>
        <div className="fuel-tracking-controls">
          {!isTracking ? (
            <button 
              onClick={handleStartTracking}
              className="fuel-tracking-btn start-btn"
              disabled={loading}
            >
              ▶️ Старт
            </button>
          ) : (
            <button 
              onClick={handleStopTracking}
              className="fuel-tracking-btn stop-btn"
              disabled={loading}
            >
              ⏹️ Стоп
            </button>
          )}
          <button 
            onClick={() => {
              setShowHistory(!showHistory);
              if (!showHistory) {
                loadSessionHistory();
              }
            }}
            className="fuel-tracking-btn history-btn"
          >
            📊 История
          </button>
        </div>
      </div>

      {/* Предупреждение об авторизации */}
      {isAuthorized === false && (
        <div className="auth-warning">
          <p>⚠️ Для сохранения истории сессий необходимо авторизоваться в системе</p>
        </div>
      )}

      <div className="fuel-tracking-info">
        <div className="tracking-status">
          <div className="status-indicator">
            <span className={`status-dot ${isTracking ? 'active' : 'inactive'}`}></span>
            <span className="status-text">
              {isTracking ? 'Отслеживание активно' : 'Отслеживание неактивно'}
            </span>
          </div>
          
          {isTracking && (
            <div className="tracking-duration">
              <strong>Длительность:</strong> {trackingDuration}
            </div>
          )}
        </div>

        {startTime && (
          <div className="tracking-period">
            <div className="period-item">
              <strong>Начало:</strong> {formatDateTime(startTime)}
            </div>
            {endTime && (
              <div className="period-item">
                <strong>Конец:</strong> {formatDateTime(endTime)}
              </div>
            )}
          </div>
        )}
      </div>

      {/* История сессий */}
      {showHistory && (
        <div className="session-history">
          <h4>История сессий отслеживания</h4>
          {sessionHistory.length > 0 ? (
            <div className="history-list">
              {sessionHistory.map((session) => (
                <div key={session.id} className="history-item">
                  <div className="history-header">
                    <span className="session-id">Сессия #{session.id}</span>
                    <span className={`session-status ${session.status.toLowerCase()}`}>
                      {session.status === 'ACTIVE' ? 'Активна' : 
                       session.status === 'COMPLETED' ? 'Завершена' : 'Отменена'}
                    </span>
                  </div>
                  <div className="history-details">
                    <div className="detail-row">
                      <span className="detail-label">Период:</span>
                      <span className="detail-value">
                        {formatDateTime(session.startTime)} - {session.endTime ? formatDateTime(session.endTime) : 'Активна'}
                      </span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Длительность:</span>
                      <span className="detail-value">{session.formattedDuration}</span>
                    </div>
                    {session.fuelConsumption !== null && (
                      <div className="detail-row">
                        <span className="detail-label">Расход:</span>
                        <span className="detail-value">{formatLiters(session.fuelConsumption)}</span>
                      </div>
                    )}
                    {session.initialFuelLevel !== null && session.finalFuelLevel !== null && (
                      <div className="detail-row">
                        <span className="detail-label">Уровни:</span>
                        <span className="detail-value">
                          {formatLiters(session.initialFuelLevel)} → {formatLiters(session.finalFuelLevel)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="no-history">История сессий пуста</p>
          )}
        </div>
      )}

      {/* Статистика топлива */}
      {realtimeData && realtimeData.consumptionStats && (
        <div className="fuel-current-stats">
          <h4>Текущая статистика топлива</h4>
          <div className="update-info">
            <span className="update-interval">🔄 Обновление каждые 5 секунд</span>
          </div>
          <div className="current-stats-grid">
            <div className="stat-item">
              <span className="stat-label">Общий расход:</span>
              <span className="stat-value">{formatLiters(realtimeData.consumptionStats.totalConsumption)}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Пополнение:</span>
              <span className="stat-value">{formatLiters(realtimeData.consumptionStats.totalRefill)}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Чистое изменение:</span>
              <span className="stat-value">{formatLiters(Math.abs(realtimeData.consumptionStats.netChange || 0))}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Активных датчиков:</span>
              <span className="stat-value">{realtimeData.consumptionStats.totalSensors || 0}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Точек данных:</span>
              <span className="stat-value">{realtimeData.dataPoints || 0}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Период мониторинга:</span>
              <span className="stat-value">{realtimeData.minutes} мин</span>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="fuel-tracking-error">
          <p>{error}</p>
        </div>
      )}

      {loading && (
        <div className="fuel-tracking-loading">
          <div className="loading-spinner"></div>
          <p>Загрузка данных...</p>
        </div>
      )}

      {fuelStats && (
        <div className="fuel-stats">
          <h4>Статистика за период</h4>
          <div className="stats-grid">
            <div className="stat-item">
              <span className="stat-label">Мин. уровень:</span>
              <span className="stat-value">{formatLiters(fuelStats.minLiters)}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Макс. уровень:</span>
              <span className="stat-value">{formatLiters(fuelStats.maxLiters)}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Средний уровень:</span>
              <span className="stat-value">{formatLiters(fuelStats.avgLiters)}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Точек данных:</span>
              <span className="stat-value">{fuelStats.totalDataPoints || 0}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Датчиков:</span>
              <span className="stat-value">{fuelStats.sensorCounts ? Object.keys(fuelStats.sensorCounts).length : 0}</span>
            </div>
          </div>
        </div>
      )}

      {consumptionData && consumptionData.consumptionStats && (
        <div className="consumption-stats">
          <h4>Статистика потребления</h4>
          <div className="stats-grid">
            <div className="stat-item">
              <span className="stat-label">Общий расход:</span>
              <span className="stat-value">{formatLiters(consumptionData.consumptionStats.totalConsumption)}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Пополнение:</span>
              <span className="stat-value">{formatLiters(consumptionData.consumptionStats.totalRefill)}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Чистое изменение:</span>
              <span className="stat-value">{formatLiters(Math.abs(consumptionData.consumptionStats.netChange || 0))}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Средний расход/час:</span>
              <span className="stat-value">{(consumptionData.avgConsumptionPerHour || 0).toFixed(2)} л/час</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Период (часов):</span>
              <span className="stat-value">{consumptionData.periodHours || 0}</span>
            </div>
          </div>
        </div>
      )}

      {fuelData && fuelData.data && fuelData.data.length > 0 && (
        <div className="fuel-data-summary">
          <h4>Данные за период</h4>
          <div className="data-summary">
            <p><strong>Всего точек данных:</strong> {fuelData.data.length}</p>
            <p><strong>Период:</strong> {fuelData.data[0]?.time ? formatDateTime(fuelData.data[0].time) : '—'} - {fuelData.data[fuelData.data.length - 1]?.time ? formatDateTime(fuelData.data[fuelData.data.length - 1].time) : '—'}</p>
            <p><strong>Датчиков:</strong> {new Set(fuelData.data.filter(d => d.sensorNumber).map(d => d.sensorNumber)).size}</p>
          </div>
        </div>
      )}

      {realtimeData && realtimeData.consumptionStats && realtimeData.consumptionStats.sensors && (
        <div className="realtime-sensors">
          <h4>Датчики топлива (текущие)</h4>
          <div className="sensors-grid">
            {Object.values(realtimeData.consumptionStats.sensors).map((sensor) => (
              <div key={sensor?.sensorNumber || 'unknown'} className="sensor-item">
                <div className="sensor-header">
                  <span className="sensor-name">Датчик №{sensor?.sensorNumber || 'N/A'}</span>
                  <span className="sensor-status active">
                    Активен
                  </span>
                </div>
                <div className="sensor-data">
                  <div className="sensor-value">
                    <span className="value-label">Текущий уровень:</span>
                    <span className="value-data">{formatLiters(sensor?.currentLevel)}</span>
                  </div>
                  <div className="sensor-value">
                    <span className="value-label">Мин. уровень:</span>
                    <span className="value-data">{formatLiters(sensor?.minLevel)}</span>
                  </div>
                  <div className="sensor-value">
                    <span className="value-label">Макс. уровень:</span>
                    <span className="value-data">{formatLiters(sensor?.maxLevel)}</span>
                  </div>
                  <div className="sensor-value">
                    <span className="value-label">Расход:</span>
                    <span className="value-data">{formatLiters(sensor?.consumption)}</span>
                  </div>
                  <div className="sensor-value">
                    <span className="value-label">Пополнение:</span>
                    <span className="value-data">{formatLiters(sensor?.refill)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Накопленные данные */}
      {(allFuelData.length > 0 || allFuelStats.length > 0 || allConsumptionData.length > 0 || allRealtimeData.length > 0) && (
        <div className="accumulated-data">
          <h4>Накопленные данные ({allRealtimeData.length} записей)</h4>
          
          {/* Статистика по накопленным данным */}
          <div className="accumulated-stats">
            <div className="stat-item">
              <span className="stat-label">Всего записей реального времени:</span>
              <span className="stat-value">{allRealtimeData.length}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Всего периодов данных:</span>
              <span className="stat-value">{allFuelData.length}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Всего статистик:</span>
              <span className="stat-value">{allFuelStats.length}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Всего данных потребления:</span>
              <span className="stat-value">{allConsumptionData.length}</span>
            </div>
          </div>

          {/* Последние данные реального времени */}
          {allRealtimeData.length > 0 && (
            <div className="recent-realtime-data">
              <h5>Последние данные реального времени</h5>
              <div className="data-list">
                {allRealtimeData.slice(-5).map((data, index) => {
                  // Вычисляем текущий уровень топлива из датчиков
                  let currentLevel = 0;
                  if (data.consumptionStats && data.consumptionStats.sensors) {
                    const sensors = data.consumptionStats.sensors;
                    if (sensors && typeof sensors === 'object') {
                      currentLevel = Object.values(sensors)
                        .reduce((sum, sensor) => sum + (sensor?.currentLevel || 0), 0);
                    }
                  }

                  return (
                    <div key={index} className="data-item">
                      <div className="data-header">
                        <span className="data-time">
                          {data.consumptionStats ? `${data.minutes} мин мониторинга` : 'Нет данных'}
                        </span>
                        <span className="data-points">{data.dataPoints || 0} точек данных</span>
                      </div>
                      {data.consumptionStats && (
                        <div className="data-details">
                          <span>Текущий уровень: {formatLiters(currentLevel)}</span>
                          <span>Расход: {formatLiters(data.consumptionStats.totalConsumption)}</span>
                          <span>Пополнение: {formatLiters(data.consumptionStats.totalRefill)}</span>
                          {data.consumptionStats.sensors && (
                            <div className="sensors-summary">
                              <span>Датчики:</span>
                              {Object.values(data.consumptionStats.sensors).map((sensor, sensorIndex) => (
                                <span key={sensorIndex} className="sensor-info">
                                  №{sensor?.sensorNumber}: {formatLiters(sensor?.currentLevel)}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Последние данные за периоды */}
          {allFuelData.length > 0 && (
            <div className="recent-period-data">
              <h5>Последние данные за периоды</h5>
              <div className="data-list">
                {allFuelData.slice(-3).map((data, index) => {
                  // Вычисляем средний уровень топлива за период
                  let avgLevel = 0;
                  let minLevel = Infinity;
                  let maxLevel = 0;
                  
                  if (data.data && data.data.length > 0) {
                    const levels = data.data.map(d => d.liters || 0).filter(level => level > 0);
                    if (levels.length > 0) {
                      avgLevel = levels.reduce((sum, level) => sum + level, 0) / levels.length;
                      minLevel = Math.min(...levels);
                      maxLevel = Math.max(...levels);
                    }
                  }

                  return (
                    <div key={index} className="data-item">
                      <div className="data-header">
                        <span className="data-time">
                          {data.data && data.data.length > 0 ? `${data.data.length} точек данных` : 'Нет данных'}
                        </span>
                      </div>
                      {data.data && data.data.length > 0 && (
                        <div className="data-details">
                          <span>Период: {formatDateTime(data.data[0].time)} - {formatDateTime(data.data[data.data.length - 1].time)}</span>
                          <span>Датчиков: {new Set(data.data.filter(d => d.sensorNumber).map(d => d.sensorNumber)).size}</span>
                          {avgLevel > 0 && (
                            <>
                              <span>Средний уровень: {formatLiters(avgLevel)}</span>
                              <span>Мин/Макс: {formatLiters(minLevel)} / {formatLiters(maxLevel)}</span>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FuelTracking; 